/**
 * src/services/weekly.api.ts 의 짝.
 *
 * Weekly 는 만들어 두지 않는다 — 부를 때마다 db.dailies 에서 모은다. 보는 주의 앞 주(지난주)는 제출한
 * Daily 만 세고, 보는 주는 계획(진행 예정)과 단계 일정만 읽는다. 전 직원이 본다.
 */
import { HttpResponse, http } from "msw";
import { shiftDate, weekStartOf } from "@/lib/dates";
import type { Weekly, WeeklyEvent, WeeklyMember, WeeklyProject } from "@/types";
import type { MockDaily } from "../data/dailies";
import { db } from "../db";
import { latency, requireUser } from "../http";
import { phaseState } from "./projects";

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);
const round = (value: number) => Math.round(value * 100) / 100;

const between = (daily: MockDaily, from: string, to: string) =>
  daily.work_date >= from && daily.work_date <= to;
const submitted = (from: string, to: string) =>
  db.dailies.filter((daily) => daily.status === "submitted" && between(daily, from, to));

const usageOf = (projectId: string) =>
  db.projectDetails.find((row) => row.project_id === projectId && row.field_id === "field-usage")
    ?.value_text ?? "";

const mergeLines = (texts: string[]) => [
  ...new Set(texts.flatMap((text) => text.split("\n").map((line) => line.trim())).filter(Boolean)),
];

function hoursOf(dailies: MockDaily[]) {
  const regular = round(sum(dailies.map((d) => d.regular_hours)));
  const overtime = round(sum(dailies.map((d) => d.overtime_hours)));
  return { regular, overtime, total: round(regular + overtime) };
}

function lineOf(lines: Map<string, WeeklyProject>, projectId: string, today: string) {
  const existing = lines.get(projectId);
  if (existing) return existing;
  const project = db.projects.find((p) => p.id === projectId);
  const open = db.projectPhases
    .filter((phase) => phase.project_id === projectId && phase.done_date === null)
    .sort((a, b) => a.sort_order - b.sort_order);
  const phase = open.find((p) => p.start_date !== null) ?? open[0];
  const line: WeeklyProject = {
    project_id: projectId,
    code: project?.code ?? null,
    name: project?.name ?? "",
    color: project?.color ?? "",
    usage: usageOf(projectId),
    status: project?.status ?? "active",
    phase: phase
      ? { name: phase.name, status: phaseState(phase, today).status, due_date: phase.due_date }
      : null,
    hours: 0,
    plan_hours: 0,
    plan_achievement: null,
    members: [],
    plans: [],
  };
  lines.set(projectId, line);
  return line;
}

function buildWeekly(weekOf: string): Weekly {
  const weekStart = weekStartOf(weekOf);
  const weekEnd = shiftDate(weekStart, 6);
  const lastStart = shiftDate(weekStart, -7);
  const lastEnd = shiftDate(weekStart, -1);
  const today = new Date().toISOString().slice(0, 10);
  const last = submitted(lastStart, lastEnd);
  const lines = new Map<string, WeeklyProject>();

  for (const daily of [...last].sort((a, b) => a.work_date.localeCompare(b.work_date))) {
    const name = db.users.find((u) => u.id === daily.user_id)?.name ?? daily.user_id;
    for (const row of daily.rows) {
      if (!row.project_id) continue;
      const line = lineOf(lines, row.project_id, today);
      let member: WeeklyMember | undefined = line.members.find((m) => m.user_id === daily.user_id);
      if (!member) {
        member = { user_id: daily.user_id, user_name: name, hours: 0, descriptions: [], days: [] };
        line.members.push(member);
      }
      member.hours = round(member.hours + row.actual_hours);
      member.descriptions.push(row.description);
      member.days.push({
        work_date: daily.work_date,
        hours: row.actual_hours,
        description: row.description,
      });
      line.hours = round(line.hours + row.actual_hours);
      line.plan_hours = round(line.plan_hours + row.plan_hours);
    }
  }
  for (const line of lines.values()) {
    line.plan_achievement = line.plan_hours ? round(line.hours / line.plan_hours) : null;
    line.members.sort((a, b) => a.user_name.localeCompare(b.user_name, "ko"));
    for (const member of line.members) member.descriptions = mergeLines(member.descriptions);
  }

  // 이번 주 계획 — 제출 전이어도 계획은 계획이다.
  const plans = new Map<string, string[]>();
  for (const daily of db.dailies.filter((d) => between(d, weekStart, weekEnd))) {
    for (const row of daily.rows) {
      if (!row.project_id || !row.plan_description) continue;
      lineOf(lines, row.project_id, today);
      plans.set(row.project_id, [...(plans.get(row.project_id) ?? []), row.plan_description]);
    }
  }
  for (const [projectId, texts] of plans) lines.get(projectId)!.plans = mergeLines(texts);

  const events: WeeklyEvent[] = db.projectPhases.flatMap((phase) => {
    const project = db.projects.find((p) => p.id === phase.project_id);
    if (!project) return [];
    const dates = [
      ["start", phase.start_date],
      ["due", phase.due_date],
      ["done", phase.done_date],
    ] as const;
    return dates
      .filter(([, day]) => day !== null && day >= weekStart && day <= weekEnd)
      .map(([kind, day]) => ({
        project_id: project.id,
        code: project.code,
        name: project.name,
        color: project.color,
        phase_name: phase.name,
        kind,
        day: day!,
      }));
  });

  const projects = [...lines.values()].sort(
    (a, b) =>
      Number(a.code === null) - Number(b.code === null) ||
      (a.code ?? "").localeCompare(b.code ?? "") ||
      a.name.localeCompare(b.name, "ko")
  );
  const planHours = sum(projects.map((p) => p.plan_hours));
  const nonProjectRows = last.flatMap((daily) => daily.rows.filter((row) => !row.project_id));
  const previous = hoursOf(submitted(shiftDate(lastStart, -7), shiftDate(lastStart, -1)));
  const inLastWeek = (day: string | null) => day !== null && day >= lastStart && day <= lastEnd;
  return {
    week_start: weekStart,
    last_week_start: lastStart,
    hours: {
      ...hoursOf(last),
      previous_total: previous.total,
      previous_overtime: previous.overtime,
    },
    plan_achievement: planHours ? round(sum(projects.map((p) => p.hours)) / planHours) : null,
    milestones_done: db.projectPhases.filter((p) => inLastWeek(p.done_date)).length,
    milestones_due: db.projectPhases.filter((p) => inLastWeek(p.due_date)).length,
    projects,
    non_project: db.dailyCategories
      .map((category) => ({
        category_id: category.id,
        name: category.name,
        hours: round(
          sum(
            nonProjectRows.filter((r) => r.category_id === category.id).map((r) => r.actual_hours)
          )
        ),
      }))
      .filter((row) => row.hours > 0),
    events: events.sort((a, b) => a.day.localeCompare(b.day)),
  };
}

export const weeklyHandlers = [
  http.get("/api/v1/weekly/", async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const weekOf = new URL(request.url).searchParams.get("week_of") ?? "";
    return HttpResponse.json(buildWeekly(weekOf));
  }),
];
